<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">

    <!-- HEADER -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
      <div>
        <h1 class="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
          <Library class="w-8 h-8 text-brand-600" />
          Catálogo de Cuentas
        </h1>
        <p class="mt-1 text-slate-500">Administra la estructura contable y niveles de mayorización.</p>
      </div>
      <button
        @click="openForm()"
        class="inline-flex items-center justify-center px-4 py-2.5 bg-brand-600 text-white font-medium rounded-xl shadow-lg shadow-brand-500/30 hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 transition-all"
      >
        <Plus class="w-5 h-5 mr-2" />
        Nueva Cuenta
      </button>
    </div>

    <!-- CONTROLES Y BUSCADOR -->
    <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 mb-6 p-4">
      <div class="relative max-w-md">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search class="h-5 w-5 text-slate-400" />
        </div>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Buscar por código o nombre..."
          class="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-all text-slate-700 dark:text-slate-200"
        >
      </div>
    </div>

    <!-- TABLA / ÁRBOL DE CUENTAS -->
    <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
              <th class="py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider w-32">Código</th>
              <th class="py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Nombre de la Cuenta</th>
              <th class="py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tipo / Naturaleza</th>
              <th class="py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Nivel Asiento</th>
              <th class="py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 dark:divide-slate-700/50">
            <tr
              v-for="account in filteredAccounts"
              :key="account.id"
              class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group"
              :class="{ 'bg-slate-50/50': account.code.length <= 2 }"
            >
              <td class="py-3 px-6 whitespace-nowrap">
                <span class="font-mono text-sm" :class="account.is_selectable ? 'text-slate-600' : 'text-slate-900 font-bold'">
                  {{ account.code }}
                </span>
              </td>

              <!-- INDENTACIÓN VISUAL -->
              <td class="py-3 px-6">
                <div class="flex items-center" :style="{ paddingLeft: ((account.code.length - 1) * 0.75) + 'rem' }">
                  <span :class="account.is_selectable ? 'text-slate-600' : 'text-slate-900 font-bold'">
                    {{ account.name }}
                  </span>
                </div>
              </td>

              <!-- BADGES -->
              <td class="py-3 px-6 whitespace-nowrap">
                <div class="flex items-center gap-2">
                  <span class="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide rounded-md bg-slate-100 text-slate-600 border border-slate-200">
                    {{ translateType(account.account_type) }}
                  </span>
                  <span
                    class="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide rounded-md border"
                    :class="account.nature === 'DEBIT' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'"
                  >
                    {{ account.nature === 'DEBIT' ? 'Deudora' : 'Acreedora' }}
                  </span>
                </div>
              </td>

              <td class="py-3 px-6 whitespace-nowrap text-center">
                <div v-if="account.is_selectable" class="inline-flex items-center justify-center p-1.5 bg-brand-50 text-white rounded-lg" title="Permite registrar movimientos">
                  <Layers class="w-4 h-4" />
                </div>
                <span v-else class="text-xs text-slate-400 font-medium">Cuenta Mayor</span>
              </td>

              <td class="py-3 px-6 whitespace-nowrap text-right">
                <div class="flex items-center justify-end gap-2 group-hover:opacity-100 ">
                  <button @click="openForm(account)" class="p-2 text-slate-400 hover:text-white hover:bg-brand-50 rounded-lg transition-colors">
                    <Edit class="w-4 h-4" />
                  </button>
                  <button @click="confirmDelete(account.id)" class="p-2 text-slate-400 hover:text-white hover:bg-red-900 rounded-lg transition-colors">
                    <Trash2 class="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="filteredAccounts.length === 0">
              <td colspan="5" class="py-12 text-center text-slate-500">
                <Library class="w-12 h-12 mx-auto mb-3 text-slate-300" />
                No se encontraron cuentas contables.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- MODAL FORMULARIO -->
    <AccountForm
      :show="showModal"
      :accountToEdit="selectedAccount"
      @close="closeForm"
      @saved="fetchAccounts"
    />

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Library, Plus, Search, Layers, Edit, Trash2 } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';
import Swal from 'sweetalert2';
import AccountForm from './Form.vue';

const { showSuccess, showError } = useToast();

const accounts = ref([]);
const searchQuery = ref('');
const showModal = ref(false);
const selectedAccount = ref(null);

const fetchAccounts = async () => {
  const user = SecureStorage.getItem('llevo_user');
  if (!user) return;
  try {
    const response = await apiClient.get(`/acc_accounts?company_id=${user.company}`);
    let data = response.data.data || response.data;
    accounts.value = data.sort((a, b) => a.code.localeCompare(b.code));
  } catch (error) {
    showError('No se pudo cargar el catálogo.');
  }
};

onMounted(fetchAccounts);

const filteredAccounts = computed(() => {
  if (!searchQuery.value) return accounts.value;
  const q = searchQuery.value.toLowerCase();
  return accounts.value.filter(acc =>
    acc.code.toLowerCase().includes(q) || acc.name.toLowerCase().includes(q)
  );
});

const translateType = (type) => {
  const types = { 'ASSET': 'Activo', 'LIABILITY': 'Pasivo', 'EQUITY': 'Patrimonio', 'REVENUE': 'Ingreso', 'EXPENSE': 'Gasto' };
  return types[type] || type;
};

const openForm = (account = null) => {
  selectedAccount.value = account ? { ...account } : null;
  showModal.value = true;
};

const closeForm = () => {
  showModal.value = false;
  selectedAccount.value = null;
};

const confirmDelete = (id) => {
  Swal.fire({
    title: '¿Eliminar Cuenta?',
    text: "No podrás eliminarla si tiene movimientos contables.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    cancelButtonColor: '#94a3b8',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await apiClient.delete(`/acc_accounts/${id}`);
        showSuccess('Cuenta eliminada');
        fetchAccounts();
      } catch (error) {
        showError('La cuenta está en uso y no puede eliminarse.');
      }
    }
  });
};
</script>